import { State } from '@meraki-internal/state';
import dayjs from 'dayjs';
import { WithLinks } from '../caremap/WithLinks';
import { ICareMap } from '../caremap/ICareMap';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { IInsight, insightMediaType } from './IInsight';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { isExpectedFetchError } from '../support/isExpectedFetchError';
import { parseInsightRef } from './insightRef';

// How long an insight stays eligible for the unprompted surfaces. Sibling of the API's push-candidate
// age cap: once an observation is this old, presenting it as something Bea just noticed is misleading.
const PROACTIVE_MAX_AGE_DAYS = 30;

export class InsightsModel extends State<Record<string, never>> {
    constructor(
        private careMap: WithLinks<ICareMap>,
        private factory: CaremapInsightsModelFactory
    ) {
        super({});
    }

    private insights: IInsight[] = [];

    hasLoaded = false;

    // Set when the initial fetch failed, so surfaces can render an inline retry instead of silently
    // falling through to an empty state. Cleared at the start of each (re)load attempt.
    loadFailed = false;

    // Optimistic field changes (dismiss / mark-seen / feedback) with an in-flight or recently-issued
    // write, keyed by insightId. A realtime refetch (load/reconcile) rebuilds the array from the
    // server, which may not yet reflect a just-issued mutation; re-applying these over the fresh list
    // keeps the optimistic value visible so e.g. a just-dismissed insight can't momentarily reappear.
    // These are all per-user, one-way fields, so the only reason the server would still disagree is
    // that it hasn't caught up yet -- an overlay entry is therefore dropped only once a refetch shows
    // the server already reflects it (see applyPendingMutations), not merely when the write resolves.
    // Clearing on write-resolution alone would leave a window where a stale read already in flight when
    // the write committed resolves afterwards and clobbers the optimistic value back.
    private pendingMutations = new Map<string, Partial<IInsight>>();

    private setPending = (insightId: string, patch: Partial<IInsight>) => {
        this.pendingMutations.set(insightId, { ...this.pendingMutations.get(insightId), ...patch });
    };

    private clearPending = (insightId: string, key: keyof IInsight) => {
        const patch = this.pendingMutations.get(insightId);
        if (!patch) {
            return;
        }
        delete patch[key];
        if (Object.keys(patch).length === 0) {
            this.pendingMutations.delete(insightId);
        }
    };

    private feedbackMatches = (a: IInsight['myFeedback'], b: IInsight['myFeedback']): boolean => {
        return a?.helpful === b?.helpful && a?.note === b?.note;
    };

    // Applied after every load/reconcile. For each optimistically-changed field: if the freshly-fetched
    // server value already matches the optimistic one, the write has landed -- drop the overlay entry;
    // otherwise the read is still stale, so re-assert the optimistic value over it.
    private applyPendingMutations = (insights: IInsight[]) => {
        if (this.pendingMutations.size === 0) {
            return;
        }
        for (const insight of insights) {
            const patch = this.pendingMutations.get(insight.insightId);
            if (!patch) {
                continue;
            }
            if (patch.isDismissed !== undefined) {
                if (insight.isDismissed === patch.isDismissed) {
                    delete patch.isDismissed;
                } else {
                    insight.isDismissed = patch.isDismissed;
                }
            }
            if (patch.isSeen !== undefined) {
                if (insight.isSeen === patch.isSeen) {
                    delete patch.isSeen;
                } else {
                    insight.isSeen = patch.isSeen;
                }
            }
            if (patch.myFeedback !== undefined) {
                if (this.feedbackMatches(insight.myFeedback, patch.myFeedback)) {
                    delete patch.myFeedback;
                } else {
                    insight.myFeedback = patch.myFeedback;
                }
            }
            if (Object.keys(patch).length === 0) {
                this.pendingMutations.delete(insight.insightId);
            }
        }
    };

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        if (message.resourceChangeType !== 'changed') {
            return;
        }

        const insightsHref = this.careMap.links.insights?.href;
        if (!insightsHref || message.resourceToRefresh.toLowerCase() !== insightsHref.toLowerCase()) {
            return;
        }

        try {
            await this.reconcile();
        } catch (err: any) {
            // reconcile() handles its own (expected transient) fetch errors internally, so this outer
            // catch is only a net for an unexpected error; the isExpectedFetchError guard is kept for
            // symmetry with MemoriesModel.onWebsocketMessage.
            if (!isExpectedFetchError(err)) {
                this.factory.logger.error(`InsightsModel.onWebsocketMessage failed reloading insights. Inner error: ${err.toString()}`);
            }
        }
    };

    load = async () => {
        const insightsLink = this.careMap.links.insights;

        // Viewers without get-private access aren't returned this link — treat as "no insights"
        // rather than erroring, so the UI can render an empty state instead of a load failure.
        if (!insightsLink) {
            this.insights = [];
            this.hasLoaded = true;
            this.loadFailed = false;
            this.setState({});
            return;
        }

        this.loadFailed = false;
        try {
            const insights: IInsight[] = await this.factory.apiClient.get(insightsLink, insightMediaType);
            this.applyPendingMutations(insights);
            this.insights = insights;
            this.hasLoaded = true;
        } catch (err: any) {
            // Surface an inline retry (loadFailed) instead of throwing to the error boundary or a
            // global alert; hasLoaded stays false so a retry re-attempts the fetch.
            this.loadFailed = true;
            this.factory.logger.error(`InsightsModel.load failed to fetch insights: ${err.toString()}`);
        } finally {
            this.setState({});
        }
    };

    // Catch-up merge used by the foreground/reconnect reconciler and by a websocket 'changed' match:
    // re-fetch the server's insights and converge the in-memory list, preserving each surviving row's
    // object identity (Object.assign in place) so an already-rendered list never flashes a skeleton,
    // while following the server's order so a newly-arrived insight lands in its correct (newest-first)
    // position instead of at the end. Same spirit as MemoriesModel.reconcile / NotesModel.reconcile.
    // Pending optimistic mutations are re-applied so an in-flight dismiss/seen/feedback survives the
    // refetch. Never flips hasLoaded back to false, but a successful reconcile does mark the model
    // loaded (and clears loadFailed), so it doubles as recovery from a failed initial load.
    reconcile = async (): Promise<void> => {
        const insightsLink = this.careMap.links.insights;
        if (!insightsLink) {
            return;
        }

        let server: IInsight[];
        try {
            server = await this.factory.apiClient.get(insightsLink, insightMediaType);
        } catch (err: any) {
            // a transient/aborted fetch is expected for this best-effort catch-up — it retries on the
            // next trigger. Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.factory.logger.error(`InsightsModel.reconcile failed to fetch insights: ${err.toString()}`);
            }
            return;
        }

        // Iterate the server list (its order is authoritative) and reuse the existing object for a
        // surviving row so identity is preserved; rows the server no longer returns are dropped.
        const existingById = new Map(this.insights.map(insight => [insight.insightId, insight]));
        const reconciled: IInsight[] = server.map(serverInsight => {
            const existing = existingById.get(serverInsight.insightId);
            if (existing) {
                Object.assign(existing, serverInsight);
                return existing;
            }
            return serverInsight;
        });

        this.applyPendingMutations(reconciled);
        this.insights = reconciled;
        this.hasLoaded = true;
        this.loadFailed = false;
        this.setState({});
    };

    getInsights = (): IInsight[] => {
        return this.insights;
    };

    // Whether this reader is allowed the insights at all. The server withholds the link from a viewer
    // without get-private access, and load() turns that into an empty list rather than a failure — so
    // a surface that wants to SAY "not shared with you" rather than "nothing here" has to ask, and has
    // to ask the same object load() reads. A surface deriving it from its own care map instance can
    // disagree with the fetch and tell somebody their record is private while showing them its score.
    isShared = (): boolean => {
        return Boolean(this.careMap.links.insights);
    };

    getActive = (): IInsight[] => {
        return this.insights.filter(insight => !insight.isDismissed);
    };

    // The api lists the reader's own private insights with every care map's, so the Insights page can
    // show them as "Only you". Anything describing the care recipient reads these instead: a personal
    // journal is about the reader, and counting it would score the caregiver's week as the care recipient's.
    getActiveAboutCareMap = (): IInsight[] => {
        return this.getActive().filter(insight => insight.visibility === 'care-map');
    };

    getDismissedAboutCareMap = (): IInsight[] => {
        return this.getDismissed().filter(insight => insight.visibility === 'care-map');
    };

    // Insights we're allowed to push at the user unprompted (home section, note cards). Low-confidence
    // insights are stored but never proactive — they surface only when explicitly pulled, so getActive()
    // and getInsights() deliberately keep them for those pull surfaces. Stale ones drop out of the
    // unprompted surfaces too: an observation nobody has superseded in a month is no longer worth
    // presenting as current, and the Insights page still lists it.
    getActiveProactive = (): IInsight[] => {
        const freshCutoff = dayjs().subtract(PROACTIVE_MAX_AGE_DAYS, 'days');
        return this.getActive().filter(insight =>
            insight.confidence !== 'low' && dayjs(insight.createdAt).isAfter(freshCutoff)
        );
    };

    // Insights the current user has marked not useful (dismissed). The Insights page pulls these into
    // its "Not useful" archive so nothing is ever lost -- getActive() filters them out of the main list.
    getDismissed = (): IInsight[] => {
        return this.insights.filter(insight => insight.isDismissed);
    };

    getForNote = (noteId: string): IInsight[] => {
        return this.getActiveProactive().filter(insight =>
            insight.evidence.some(evidence => {
                const parsed = parseInsightRef(evidence.ref);
                return parsed && parsed.sourceId === noteId && parsed.contentType === 'note';
            })
        );
    };

    markSeen = async (insight: IInsight) => {
        const wasSeen = insight.isSeen;
        insight.isSeen = true;
        this.setPending(insight.insightId, { isSeen: true });
        this.setState({});

        try {
            await this.factory.apiClient.put(insight.links.seen, {});
        } catch (err: any) {
            insight.isSeen = wasSeen;
            this.clearPending(insight.insightId, 'isSeen');
            this.setState({});
            this.factory.logger.error(`InsightsModel.markSeen failed for insightId ${insight.insightId}. Inner error: ${err.toString()}`);
        }
    };

    dismiss = async (insight: IInsight, surface: string) => {
        const wasDismissed = insight.isDismissed;
        insight.isDismissed = true;
        this.setPending(insight.insightId, { isDismissed: true });
        this.setState({});

        if (!wasDismissed) {
            this.factory.tracking.track('Insight Dismissed', () => ({
                'Surface': surface,
                'Type': insight.type,
                'Confidence': insight.confidence
            }));
        }

        try {
            await this.factory.apiClient.put(insight.links.dismissed, {});
        } catch (err: any) {
            insight.isDismissed = wasDismissed;
            this.clearPending(insight.insightId, 'isDismissed');
            this.setState({});
            this.factory.logger.error(`InsightsModel.dismiss failed for insightId ${insight.insightId}. Inner error: ${err.toString()}`);
        }
    };

    sendFeedback = async (params: { insight: IInsight; helpful: boolean; surface: string; note?: string }) => {
        const { insight, helpful, surface, note } = params;
        const previousFeedback = insight.myFeedback;
        const feedback = { helpful, note, at: new Date().toISOString() };
        insight.myFeedback = feedback;
        this.setPending(insight.insightId, { myFeedback: feedback });
        this.setState({});

        this.factory.tracking.track('Insight Feedback', () => ({
            'Helpful': helpful,
            // the stored note is a NOT_USEFUL_REASONS key; 'Reason' matches what the API's
            // 'Insight Vetted Out' calls the same idea, so the two read alike in a breakdown.
            // Omitted rather than sent as undefined when the user skipped the reason step.
            ...(note !== undefined && { 'Reason': note }),
            'Surface': surface,
            'Type': insight.type,
            'Confidence': insight.confidence
        }));

        try {
            await this.factory.apiClient.post(
                insight.links.feedback,
                { helpful, note },
                { accept: insightMediaType, 'content-type': insightMediaType }
            );
        } catch (err: any) {
            insight.myFeedback = previousFeedback;
            this.clearPending(insight.insightId, 'myFeedback');
            this.setState({});
            this.factory.logger.error(`InsightsModel.sendFeedback failed for insightId ${insight.insightId}. Inner error: ${err.toString()}`);
        }
    };
}
